<template>
    <div class="plan-card">
        <slot name="plan-card-badge"></slot>
        <div class="plan-card__wrapper">
            <div class="plan-card__header">
                <img class="plan-card__header__icon" :src="`/img/${planData.icon}.png`"/>
                <p class="text plan-card__title">
                    {{ planData.title }}
                </p>
                <p class="text plan-card__description">
                    {{ planData.description }}
                </p>
            </div>
            <div class="plan-card__body">
                <slot name="plan-card-body"></slot>
                <CheckoutButton
                    v-if="planData.action.type === 'fill'"
                    id="pricing-upgrade-now-button" 
                    location="Pricing Page"
                    :btn-text="`${user ? 'Upgrade Now' : 'Get Started'}`"
                    class="plan-card__fill plan-card__action"
                    :price="priceData"
                />
                <button 
                    v-else
                    class="plan-card__action"
                    :disabled="inactiveBasic"
                    :class="[
                        `plan-card__${planData.action.type}`, 
                        {'plan-card__inactive-basic': inactiveBasic}
                    ]"
                    @click="$emit('handleCardAction', planData.title)"
                >
                    {{planData.action.text}}
                </button>
            </div>
        </div>
        <div class="plan-card__footer">
            <ul class="plan-card__options">
                <li 
                    v-for="item in planData.options" 
                    :key="item.text" 
                    class="plan-card__option"
                >
                    <Icon icon="pricing-check-icon"/>
                    <p class="plan-card__option__text">
                        {{ item.text }}
                        <span 
                            v-if="item.tooltip"
                            class="cursor-pointer" 
                        >
                            <Icon 
                                v-b-tooltip.right 
                                :title="item.tooltip" 
                                icon="pricing-plancard-tooltip-icon"
                            />
                        </span>
                    </p>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import {mapGetters, mapState} from 'vuex'
import CheckoutButton from '@/components/CheckoutButton'
import Icon from '@/components/Theme/Icon'
export default {
    name: 'PlanCard',
    components: {
        Icon,
        CheckoutButton
    },
    props: {
        planData: {
            type: Object,
            default: () => {}
        },
        priceData: {
            type: Object,
            default: () => {}
        }
    },
    computed: {
        ...mapState([
            'user'
        ]),
        ...mapGetters(['inPremium']),
        inactiveBasic() {
            return this.planData.action.type === 'outline' && this.user && this.user.uid 
        }
    }
}
</script>

<style lang="scss" scoped>
$card_background: #FDFDFD;
$card_radius: 12px;
$card_padding: 45px 32px 0;
$card_title_fontsize: 18px;
$card_title_lineheight: 24px;
$card_description_fontsize: 14px;
$card_description_lineheight: 20px;
$card_font_color: #333333;
$card_bold_font: 'Eina Bold';
$card_sm_gap: 8px;
$card_xs_gap: 4px;
$card_sm_double_gap: 16px;
$card_md_gap: 10px;
$card_md_double_gap: 20px;
$card_action_outline_color: #718096;
$card_action_fill_color: #727CF5;
$card_gray_color: #E2E8F0;
$card_white_color: #ffffff;
$card_action_fill_next_color: #0ACF97;
$card_action_radius: 4px;
$card_font_semibold: 'Eina SemiBold';
$transition: all 0.2s ease;

.plan-card {
    background: #FDFDFD;
    padding: $card_padding;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    @media (max-width: 992px) {
        max-width: 504px;
        width: 100%;
        margin: 0 auto;
        border-radius: 12px !important;
        box-shadow: 8px 0px 24px rgba(8, 35, 48, 0.04), -8px 0px 24px rgba(8, 35, 48, 0.04);

        &:not(:last-child) {
            margin-bottom: 24px;
        }
    }
    &__wrapper {
        width: 100%;
        min-height: 314px;
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    &__header {
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 297px;
        &__icon {
            max-height: 92px;
        }
    }
    .text {
        margin-bottom: 0;
    }
    &__title {
        font-size: $card_title_fontsize;
        line-height: $card_title_lineheight;
        color: $card_font_color;
        font-family: $card_bold_font;
        margin-top: $card_sm_gap;
    }
    &__description {
        font-size: $card_description_fontsize;
        line-height: $card_description_lineheight;
        color: $card_font_color;
        margin-top: $card_xs_gap;
        text-align: center;
    }
    &__body {
        width: 100%;
        margin-top: auto;
    }
    &__action {
        font-size: $card_description_fontsize;
        font-family: $card_font_semibold;
        border-width: 1px;
        border-style: solid;
        padding: $card_md_gap 0;
        border-radius: $card_action_radius;
        width: 100%;
        margin-top: $card_md_double_gap;
        transition: $transition;
        span {
            font-size: $card_description_fontsize !important;
            font-family: $card_font_semibold;
        }
    }
    &__inactive-premium {
        border-color: transparent !important;
        background: #727cf566 !important;
        &:hover {
            box-shadow: unset !important;
        }
    }
    &__inactive-basic {
        color: #71809666 !important;
        border-color: #71809666 !important;

        &:hover {
            background: none !important;
            color: currentColor;
        }
    }
    &__outline {
        color: $card_action_outline_color;
        border-color: $card_action_outline_color;

        &:hover {
            background: $card_action_outline_color;
            color: $card_white_color;
        }
    }
    &__fill {
        color: $card_white_color;
        background: $card_action_fill_color;
        border-color: $card_action_fill_color;
        span {
            color: $card_white_color ;
            font-family: $card_font_semibold !important;
        }
        &:hover {
            box-shadow: 0px 4px 8px rgba(114, 124, 245, 0.45);
        }
    }
    &__fill-next {
        color: $card_white_color;
        background: $card_action_fill_next_color;
        border-color: $card_action_fill_next_color;
        &:hover {
            box-shadow: 0px 4px 10px rgba(10, 207, 151, 0.39);
        }
    }
    &__footer {
        border-top: 1px solid $card_gray_color;
        margin-top: $card_md_double_gap;
        padding-bottom: $card_md_double_gap;
        width: 100%;
    }
    &__options {
        padding-top: $card_md_double_gap;
        padding-left: 0;
    }
    &__option {
        display: flex;
        align-items: center;
        &:not(:first-child) {
            margin-top: $card_md_gap;
        }
        &__text {
            margin-bottom: 0;
            font-size: $card_description_fontsize;
            line-height: $card_description_lineheight;
            margin-left: $card_md_gap;
            img {
                filter: invert(0) sepia(1) saturate(0);
                outline: 1px solid #89929C;
                border-radius: 50%;
                transition: all 0.2s ease;
                &:hover {
                    filter: none;
                    outline: 1px solid #CBD5E0;
                }
            }
        }
    }
}
</style>